<template>
  <div id="nav">
    <nav class="navbar navbar-expand-lg" v-bind:class="bgColor">
      <div class="navbar-container">
        <!-- Logo/Brand -->
        <div class="navbar-brand">
          <router-link to="/" class="brand-link">
            <font-awesome-icon icon="home" class="brand-icon" />
          </router-link>
        </div>

        <!-- Mobile Toggle Button -->
        <button
          class="navbar-toggler"
          type="button"
          @click="toggleMenu"
          :aria-expanded="isMenuOpen"
          aria-label="Toggle navigation"
        >
          <div class="hamburger" :class="{ 'active': isMenuOpen }">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>

        <!-- Navigation Menu -->
        <div class="navbar-collapse" :class="{ 'show': isMenuOpen }" id="navbarTogglerDemo01" @click="handleMenuClick">
          <ul class="navbar-nav">
            <li class="nav-item">
              <router-link class="nav-link" to="/" exact @click.native="closeMenu">
                <font-awesome-icon icon="home" class="nav-icon" />
                <span class="nav-text">{{ $t('nav.home') }}</span>
              </router-link>
            </li>
            <li class="nav-item">
              <router-link class="nav-link" to="/events" exact @click.native="closeMenu">
                <font-awesome-icon icon="calendar-alt" class="nav-icon" />
                <span class="nav-text">{{ $t('nav.events') }}</span>
              </router-link>
            </li>
            <li class="nav-item">
              <router-link class="nav-link" to="/training-plan" exact @click.native="closeMenu">
                <font-awesome-icon icon="dumbbell" class="nav-icon" />
                <span class="nav-text">{{ $t('nav.trainingPlans') }}</span>
              </router-link>
            </li>
            <li class="nav-item">
              <router-link v-if="!isLoggedIn" class="nav-link" to="/login" exact @click.native="closeMenu">
                <font-awesome-icon icon="sign-in-alt" class="nav-icon" />
                <span class="nav-text">{{ $t('nav.login') }}</span>
              </router-link>
            </li>
            <li class="nav-item">
              <router-link v-if="isLoggedIn" class="nav-link" to="/profile" exact @click.native="closeMenu">
                <font-awesome-icon icon="user" class="nav-icon" />
                <span class="nav-text">{{ $t('nav.profile') }}</span>
              </router-link>
            </li>
            <li class="nav-item">
              <a href="#" v-if="isLoggedIn" class="nav-link" @click="logoutAndCloseMenu">
                <font-awesome-icon icon="sign-out-alt" class="nav-icon" />
                <span class="nav-text">{{ $t('nav.logout') }}</span>
              </a>
            </li>
            <li class="nav-item">
              <a
                href="https://nghienchaybo.com/blog/"
                target="_blank"
                class="nav-link"
                @click="closeMenu"
              >
                <font-awesome-icon icon="newspaper" class="nav-icon" />
                <span class="nav-text">{{ $t('nav.blog') }}</span>
              </a>
            </li>
            <li class="nav-item">
              <a
                href="https://forms.gle/ZR35ZekK2WgqR8Au7"
                target="_blank"
                class="nav-link"
                @click="closeMenu"
              >
                <font-awesome-icon icon="flag-checkered" class="nav-icon" />
                <span class="nav-text">{{ $t('nav.createEvent') }}</span>
              </a>
            </li>
          </ul>
        </div>
        <!-- Language Switcher -->
        <div class="language-switcher-container">
          <LanguageSwitcher />
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import LanguageSwitcher from '../components/LanguageSwitcher.vue'
export default {
  name: 'NavBar',
  components: { LanguageSwitcher },
  data: function () {
    return {
      bgColor: '',
      isMenuOpen: false
    }
  },
  created () {
    this.bgColor = this.getBgColor()
  },
  computed: {
    isLoggedIn: function () {
      return this.$store.state.isLoggedIn
    }
  },
  methods: {
    logout: function () {
      if (localStorage.hasOwnProperty('athlete')) {
        localStorage.removeItem('athlete')
      }
      this.$store.commit('setIsLoggedIn', false)
      window.location.reload()
    },
    logoutAndCloseMenu: function () {
      this.closeMenu()
      this.logout()
    },
    getBgColor: function () {
      const env = process.env.NODE_ENV || 'dev'
      if (env === 'dev' || env === 'stag') {
        return 'bg-warning'
      }
      return ''
    },
    toggleMenu: function () {
      this.isMenuOpen = !this.isMenuOpen
    },
    closeMenu: function () {
      this.isMenuOpen = false
    },
    handleMenuClick: function (event) {
      // Close menu when clicking on any nav-link
      if (event.target.closest('.nav-link')) {
        setTimeout(() => {
          this.closeMenu()
        }, 100)
      }
    }
  },
  mounted () {
    // Close menu when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.$el.contains(e.target)) {
        this.isMenuOpen = false
      }
    })
  },
  watch: {
    isMenuOpen (newVal) {
      if (newVal) {
        document.body.classList.add('no-scroll')
      } else {
        document.body.classList.remove('no-scroll')
      }
    }
  },
  beforeDestroy () {
    document.body.classList.remove('no-scroll')
  }
}
</script>

<style lang="scss">
@import '@/styles/sport-theme';

// Sport — light blue header

#nav {
  position: sticky;
  top: 0;
  z-index: 1001;
  background: rgba($sport-foam, 0.97);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba($sport-sky, 0.35);
  box-shadow: 0 1px 2px rgba($sport-deep, 0.05);
  width: 100%;

  @media (max-width: 991px) {
    .navbar-collapse {
      z-index: 1002;
    }
  }
}

.navbar {
  padding: 0;
  min-height: 56px;
  position: relative;
  width: 100%;
}

.navbar-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  overflow: visible;
  position: relative;
}

.navbar-brand {
  display: flex;
  align-items: center;
}

.brand-link {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  text-decoration: none;
  color: $sport-deep;
  font-weight: 700;
  font-size: 1.25rem;
  transition: background-color 0.2s ease, color 0.2s ease;
  padding: 0.4rem 0.55rem;
  border-radius: 8px;

  &:hover {
    color: $sport-primary;
    background: rgba($sport-soft, 0.6);
  }
}

.brand-icon {
  font-size: 1.45rem;
  color: $sport-deep;
}

.brand-text {
  font-weight: 800;
  letter-spacing: 1px;
}

// Mobile Toggle Button
.navbar-toggler {
  display: none;
  border: none;
  background: transparent;
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba($sport-deep, 0.3);
    border-radius: 8px;
  }

  &:hover {
    background: rgba($sport-sky, 0.2);
  }

  &:active {
    background: rgba($sport-deep, 0.1);
    transform: scale(0.95);
  }
}

.hamburger {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 24px;
  height: 20px;
  position: relative;
  transition: all 0.3s ease;

  span {
    display: block;
    width: 100%;
    height: 3px;
    background: $sport-deep;
    border-radius: 2px;
    transition: all 0.3s ease;
    transform-origin: center;
  }

  &.active {
    span:nth-child(1) {
      transform: rotate(45deg) translate(6px, 6px);
    }

    span:nth-child(2) {
      opacity: 0;
    }

    span:nth-child(3) {
      transform: rotate(-45deg) translate(6px, -6px);
    }
  }
}

// Navigation Menu
.navbar-collapse {
  display: flex;
  align-items: center;
  flex-grow: 1;
  justify-content: flex-end;
  max-width: calc(100% - 180px);
  overflow: hidden;
  min-width: 0;
}

.navbar-nav {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  padding: 0;
  list-style: none;
  flex-wrap: nowrap;
  overflow: hidden;
}

.nav-item {
  position: relative;
  padding: 0.3rem 0;
  flex-shrink: 0;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.48rem 0.72rem;
  color: $sport-primary;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.875rem;
  border-radius: 6px;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  position: relative;
  overflow: visible;
  white-space: nowrap;
  min-width: 0;
  background: transparent;
  border: 1px solid transparent;

  &:hover {
    color: $sport-deep;
    background: rgba($sport-soft, 0.72);
    border-color: rgba($sport-sky, 0.28);
    box-shadow: none;
    transform: none;
  }

  &.router-link-exact-active {
    color: $sport-deep;
    background: rgba($sport-soft, 0.92);
    font-weight: 600;
    border: 1px solid rgba($sport-sky, 0.42);
    box-shadow: none;
  }
}

.nav-icon {
  font-size: 0.85rem;
  width: 16px;
  text-align: center;
  flex-shrink: 0;
}

.nav-text {
  font-size: 0.875rem;
  flex-shrink: 0;
  font-weight: 500;
}

.language-switcher-container {
  margin-left: 0.6rem;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0.3rem 0.45rem;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba($sport-sky, 0.28);
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.88);
    border-color: rgba($sport-sky, 0.45);
    box-shadow: none;
  }
}

// Responsive Design
@media (max-width: 991px) {
  .navbar-toggler {
    display: block;
    padding: 0.75rem;
    margin-right: 0.5rem;
  }

  .navbar-collapse {
    position: fixed;
    top: 56px;
    left: 0;
    right: 0;
    background: rgba($sport-foam, 0.98);
    backdrop-filter: blur(16px);
    border-top: 1px solid rgba($sport-sky, 0.35);
    box-shadow: 0 6px 20px rgba($sport-deep, 0.08);
    opacity: 0;
    visibility: hidden;
    transform: translateY(-12px);
    transition: opacity 0.25s ease, visibility 0.25s ease, transform 0.25s ease;
    max-width: none;
    padding: 0.35rem 0 0.6rem;
    z-index: 1002;
    max-height: calc(100vh - 56px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    -webkit-tap-highlight-color: transparent;

    &.show {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }
  }

  .navbar-nav {
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    padding: 0.5rem 0;
    width: 100%;
    margin: 0;
  }

  .nav-item {
    width: 100%;
    margin: 0;
    padding: 0;
    opacity: 1;
    transform: translateX(0);
    transition: all 0.3s ease;

    &:first-child {
      margin-top: 0.5rem;
    }

    &:last-child {
      margin-bottom: 0.5rem;
    }

    // Only animate when menu is shown
    .navbar-collapse.show & {
      opacity: 1;
      transform: none;
    }
  }

  .nav-link {
    padding: 0.75rem 1.15rem;
    border-radius: 0;
    border-bottom: 1px solid rgba($sport-sky, 0.22);
    width: 100%;
    justify-content: flex-start;
    font-size: 0.95rem;
    font-weight: 500;
    min-height: 46px;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    transition: all 0.2s ease;
    cursor: pointer;
    -webkit-tap-highlight-color: rgba($sport-sky, 0.2);

    &:hover {
      background: rgba($sport-soft, 0.65);
      transform: none;
    }

    &.router-link-exact-active {
      background: rgba($sport-soft, 0.88);
      color: $sport-deep;
      font-weight: 600;

      &::after {
        display: none;
      }
    }

    &:active {
      background: rgba($sport-sky, 0.3);
      transform: scale(0.98);
    }
  }

  .nav-icon {
    font-size: 1.1rem;
    width: 20px;
    text-align: center;
    padding-right: 0;
    flex-shrink: 0;
    color: $sport-primary;
  }

  .nav-text {
    font-size: 1rem;
    flex-shrink: 0;
    color: inherit;
  }

  .language-switcher-container {
    margin-left: 0;
    padding: 0.5rem 1.15rem 0.6rem;
    margin-top: 0;
    background: transparent;
    border: none;
    box-shadow: none;

    &:hover {
      background: transparent;
      border: none;
      box-shadow: none;
    }
  }

  .navbar {
    min-height: 56px;
  }

  #nav {
    box-shadow: none;
  }
}

@media (max-width: 768px) {
  .navbar-container {
    padding: 0 1rem;
  }

  .navbar-collapse {
    padding: 0.25rem 0;
  }

  .nav-link {
    padding: 0.625rem 1rem;
    font-size: 0.9375rem;
    min-height: 44px;
  }

  .nav-icon {
    font-size: 1rem;
    width: 20px;
  }

  .nav-text {
    font-size: 0.9375rem;
  }

  .hamburger {
    width: 28px;
    height: 24px;
    gap: 5px;

    span {
      height: 3px;
      border-radius: 3px;
    }
  }

  .navbar-toggler {
    padding: 1rem;
    margin-right: 0.25rem;
  }
}

@media (max-width: 576px) {
  .navbar-container {
    padding: 0 1.25rem;
  }

  .navbar-collapse {
    padding: 0.25rem 0;
  }

  .nav-link {
    padding: 0.675rem 1rem;
    font-size: 0.95rem;
    min-height: 44px;
  }

  .nav-icon {
    font-size: 1.05rem;
    width: 22px;
  }

  .nav-text {
    font-size: 0.95rem;
  }

  .brand-link {
    font-size: 1.2rem;
  }

  .brand-icon {
    font-size: 1.35rem;
  }

  .hamburger {
    width: 32px;
    height: 28px;
    gap: 6px;

    span {
      height: 4px;
      border-radius: 4px;
    }
  }

  .navbar-toggler {
    padding: 1.25rem;
  }

  .language-switcher-container {
    padding: 0.45rem 1rem 0.5rem;
    margin-top: 0.25rem;
  }
}

@media (max-width: 480px) {
  .navbar-container {
    padding: 0 1rem;
  }

  .nav-link {
    padding: 0.65rem 1rem;
    font-size: 0.95rem;
    min-height: 44px;
    gap: 0.65rem;
  }

  .nav-icon {
    font-size: 1rem;
    width: 22px;
  }

  .nav-text {
    font-size: 0.95rem;
  }

  .brand-link {
    font-size: 1.22rem;
  }

  .brand-icon {
    font-size: 1.4rem;
  }

  .hamburger {
    width: 30px;
    height: 26px;
    gap: 6px;

    span {
      height: 3px;
      border-radius: 3px;
    }
  }

  .navbar-toggler {
    padding: 0.75rem;
  }

  .language-switcher-container {
    padding: 0 1rem;
    margin-top: 0.35rem;
  }
}

// iPhone SE specific optimizations
@media (max-width: 375px) and (max-height: 667px) {
  .navbar-collapse {
    max-height: calc(100vh - 56px);
    top: 56px;
  }

  .nav-link {
    padding: 0.875rem 1.25rem;
    min-height: 44px;
    font-size: 0.95rem;
  }

  .nav-item {
    &:first-child {
      margin-top: 0.25rem;
    }

    &:last-child {
      margin-bottom: 0.25rem;
    }
  }
}

// Desktop — compact toolbar
@media (min-width: 992px) {
  #nav {
    box-shadow: 0 1px 3px rgba($sport-deep, 0.06);
  }

  .navbar {
    min-height: 60px;
  }

  .navbar-nav {
    gap: 0.2rem;
  }

  .nav-link {
    padding: 0.5rem 0.82rem;
    font-size: 0.895rem;

    &:hover {
      transform: none;
      box-shadow: none;
    }
  }

  .nav-icon {
    font-size: 0.875rem;
    width: 16px;
  }

  .nav-text {
    font-size: 0.895rem;
    font-weight: 500;
  }

  .brand-link {
    font-size: 1.35rem;
    padding: 0.42rem 0.65rem;
  }

  .brand-icon {
    font-size: 1.55rem;
  }
}

// Extra large desktop
@media (min-width: 1200px) {
  .navbar-container {
    max-width: 1600px;
  }

  .navbar-nav {
    gap: 0.28rem;
  }

  .nav-link {
    padding: 0.52rem 0.88rem;
    font-size: 0.91rem;
  }
}

.nav-link:focus-visible {
  outline: 3px solid $sport-deep;
  outline-offset: 3px;
  border-radius: 10px;
  box-shadow: 0 0 0 4px rgba($sport-sky, 0.3);
}

.brand-link:focus-visible {
  outline: 3px solid $sport-deep;
  outline-offset: 3px;
  border-radius: 12px;
  box-shadow: 0 0 0 4px rgba($sport-sky, 0.3);
}

.nav-link,
.brand-link,
.navbar-toggler,
.language-switcher-container {
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease,
    box-shadow 0.2s ease, opacity 0.2s ease;
}
</style>

<style>
body.no-scroll {
  overflow: hidden !important;
  position: fixed;
  width: 100%;
}
</style>
